<template>
  <nav class="section-tabs" role="tablist" :aria-label="ariaLabel">
    <button
      v-for="tab in tabs"
      :key="tab.id"
      role="tab"
      :aria-selected="tab.id === modelValue"
      :class="['section-tabs__btn', { 'section-tabs__btn--active': tab.id === modelValue, 'section-tabs__btn--disabled': tab.disabled }]"
      :disabled="tab.disabled"
      @click="!tab.disabled && $emit('update:modelValue', tab.id)"
    >
      <span class="section-tabs__label">{{ tab.label }}</span>
      <span v-if="tab.badge" class="section-tabs__badge">{{ tab.badge }}</span>
      <span v-if="tab.disabled" class="section-tabs__hint">soon</span>
    </button>
  </nav>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

export interface SectionTab {
  id: string
  label: string
  disabled?: boolean
  badge?: string | number
}

export default defineComponent({
  name: 'SectionTabs',
  props: {
    modelValue: { type: String, required: true },
    tabs: { type: Array as PropType<SectionTab[]>, required: true },
    ariaLabel: { type: String, default: 'Section tabs' },
  },
  emits: ['update:modelValue'],
})
</script>

<style scoped>
.section-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0;
  margin: 0 0 24px;
  font-family: var(--font-sans);
  overflow-x: auto;
  scrollbar-width: none;
}
.section-tabs::-webkit-scrollbar { display: none; }

.section-tabs__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.55);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  border-bottom: 2px solid transparent !important;
  margin-bottom: -1px;
  transition: color 0.12s ease, border-color 0.12s ease;
  position: relative;
  outline: none;
  letter-spacing: 0.01em;
}

.section-tabs__btn:hover:not(:disabled) {
  color: rgba(255, 255, 255, 0.95);
}

.section-tabs__btn--active {
  color: #ffffff;
  border-bottom-color: #96ffc9 !important;
}

.section-tabs__btn--disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.section-tabs__badge {
  background: rgba(150, 255, 201, 0.15);
  color: #96ffc9;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  line-height: 1;
}

.section-tabs__hint {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  padding: 2px 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  line-height: 1;
}
</style>
